import { Box, Flex, Icon, Checkbox } from '@chakra-ui/react';
import { Field, FieldArray } from 'formik';
import { Draggable } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import { FaPlus } from 'react-icons/fa';
import { Trash01 } from '@kula-ai/icons/dual-tone/Trash01';
import { IconButton } from '@/components/ats';
import SvgDragAndDrop from '@/assets/icons/table/DragAndDrop';
import { KText } from '@/ui-library/components/text';
import { KInput } from '@/ui-library/components/atoms/KInput';
import { Stars, Range, YesNo, OpenQuestion } from '../scorecard-questions';
import { DescriptionField } from './Description';

const SCORE_CARD_TYPES = {
  star: { label: 'Score (stars)', Component: Stars },
  one_to_ten: { label: 'Score (1 to 10)', Component: Range },
  yes_no: { label: 'Yes/No', Component: YesNo },
  open_question: { label: 'Open question', Component: OpenQuestion },
  multiple_choice: { label: 'Single select' },
  checkbox: { label: 'Multiple select' },
};

interface ScoreCardItem {
  id: string;
  type: string;
  question?: string;
  options?: { optionText: string }[];
  mandatory?: boolean;
}

interface ScoreCardContainerProps {
  item: ScoreCardItem;
  formik: any;
  index: number;
  arrayHelpers: any;
}

export const ScoreCardContainer = ({
  item,
  formik,
  index,
  arrayHelpers,
}: ScoreCardContainerProps) => {
  const { type } = item;
  const { label, Component } = SCORE_CARD_TYPES[type] || {};

  return (
    <Draggable
      key={item.id}
      draggableId={`${item.id}`}
      index={index}
      disableInteractiveElementBlocking
    >
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            background: snapshot.isDragging && '#fff',
            boxShadow:
              snapshot.isDragging &&
              '4px 0px 10px 0px #D7DAEB1A,18px 0px 18px 0px #D7DAEB17,40px 0px 24px 0px #D7DAEB0D,70px 0px 28px 0px #D7DAEB03,110px 0px 31px 0px #D7DAEB00',
          }}
          border="1px solid"
          borderColor="border-color"
          p="12px"
          borderRadius="xs"
          mb="10px"
          data-test-id="score-card-question-container"
        >
          <Flex justifyContent="space-between" mb="12px">
            <Flex>
              <Box
                {...provided.dragHandleProps}
                cursor={snapshot.isDragging ? 'grabbing' : 'grab'}
              >
                <Icon
                  as={SvgDragAndDrop}
                  width="14px"
                  height="14px"
                  mr="md"
                  verticalAlign={'baseline'}
                />
              </Box>
              <KText size="sm-medium" data-test-id="scorecard-question-name">
                {label}
              </KText>
            </Flex>
            <Flex alignItems="center" gap="16px">
              <Flex as="label" cursor="pointer">
                <Field name={`scoreCards.${index}.mandatory`}>
                  {({ field }) => (
                    <Checkbox {...field} defaultChecked={!!field.value} />
                  )}
                </Field>
                <KText
                  size="sm-medium"
                  pl="6px"
                  data-test-id="mark-question-as-mandatory"
                >
                  Mandatory
                </KText>
              </Flex>
              <Icon
                as={Trash01}
                cursor="pointer"
                onClick={() => arrayHelpers.remove(index)}
                data-test-id="delete-question"
              />
            </Flex>
          </Flex>

          <QuestionField index={index} />
          <Box m="10px 14px">
            <DescriptionField name={`scoreCards.${index}.description`} />
          </Box>

          {Component && (
            <Flex mx="14px" mt="12px">
              <Component />
            </Flex>
          )}

          {['multiple_choice', 'checkbox'].includes(type) && (
            <OptionsField
              index={index}
              type={type}
              options={item.options || []}
              formikErrors={formik.errors}
            />
          )}
        </Box>
      )}
    </Draggable>
  );
};

const QuestionField = ({ index }) => (
  <Flex flexDir="column" mx="14px">
    <Field name={`scoreCards.${index}.question`}>
      {({ field, meta }) => (
        <KInput
          {...field}
          p="14px"
          placeholder="Enter question"
          value={field?.value || ''}
          error={meta?.error}
          isRequired={true}
          maxLength={250}
        />
      )}
    </Field>
  </Flex>
);

const OptionsField = ({ index, type, options, formikErrors }) => (
  <Flex flexDir="column" ml="14px" mt="12px" gap="12px">
    <FieldArray name={`scoreCards.${index}.options`}>
      {({ push, remove }) => (
        <>
          {options.map((opt, optIdx) => (
            <Field
              key={optIdx}
              name={`scoreCards.${index}.options.${optIdx}.optionText`}
            >
              {({ field, meta }) => (
                <Flex alignItems="center" gap="12px">
                  <Box
                    minW="24px"
                    height="24px"
                    borderRadius={type === 'multiple_choice' ? 'full' : 'xs'}
                    border="2px solid"
                    borderColor={
                      !field?.value && meta.error ? 'error-200' : 'border-color'
                    }
                  />
                  <KInput
                    error={field?.value ? '' : meta.error}
                    {...field}
                    placeholder="Write a choice"
                    value={field?.value}
                    isRequired={true}
                    maxLength={250}
                    data-test-id="scorecard-question-option"
                  />
                  <Icon
                    as={Trash01}
                    cursor="pointer"
                    onClick={() => remove(optIdx)}
                    data-test-id="delete-option-button"
                  />
                </Flex>
              )}
            </Field>
          ))}

          {formikErrors?.scoreCards?.[index]?.options &&
            typeof formikErrors.scoreCards[index].options === 'string' && (
              <KText color="error-200">
                {formikErrors.scoreCards[index].options}
              </KText>
            )}

          <IconButton
            icon={FaPlus}
            text="Add option"
            type="button"
            onClick={() => push({ optionText: '' })}
            width="fit-content"
            data-test-id="add-option-button"
          />
        </>
      )}
    </FieldArray>
  </Flex>
);
